import { SuperAdminRole } from 'lib/const/SuperAdminRole';
import { CustomerOrganization, UserOrganizationAccess } from '../types';
import { InteractiveCTA } from 'lib/api';
import { IMSFields } from 'app/pages/admin/ims/types';

enum VideoQualityType {
  'FHD' = '1080',
  '4K' = '4096',
}

enum VideoEditingType {
  'Basic' = 'Basic',
  'Advanced' = 'Advanced',
}

// DOUBLE CHECK
export interface IPackageProduct {
  id: number;
  name: string;
  value?: string;
}

export interface IPackageDetails {
  id: number;
  stripePackageId: string;
  products: any;
  maxVideosCreated: number;
  monthlyVideos: number;
  maxLength: number;
  maxQuality: VideoQualityType;
  editingType: VideoEditingType;
  title: string;
}

export interface IUser {
  packageDetails: IPackageDetails;
  features: any[];
  customerId: string;
  intercom: IIntercom;
}

//INTERCOM
export interface IIntercom {
  app_id: string;
  name: string;
  email: string;
  user_id: string;
  user_hash: string;
  cv_user_id: string;
  num_videos: string;
  free_trial: boolean;
  signup_date: string;
  purchase_date: string;
  reseller_id: string;
  reseller_name: string;
  auto: boolean;
  admin: boolean;
  wheelsTV: string;
  user_active: boolean;
  last_active: string;
  v1: boolean;
  v2: boolean;
  v3: boolean;
  covideo_3: boolean;
  covideo_4: boolean;
  covideo_4_service: boolean;
  covideo_4_sales: boolean;
}
export interface IWorkingHours {
  sunday: IWorkingDay | null;
  monday: IWorkingDay | null;
  tuesday: IWorkingDay | null;
  wednesday: IWorkingDay | null;
  thursday: IWorkingDay | null;
  friday: IWorkingDay | null;
  saturday: IWorkingDay | null;
}
export interface IWorkingDay {
  from: IWorkingDayOptions;
  to: IWorkingDayOptions;
}

export interface IWorkingDayOptions {
  hours: string;
  meridiem: string;
}
export interface IReseller {
  resellerId: string;
  resellerName: string;
  url: string;
  email: string;
  phoneNumber: string;
  pls: string;
  customerId: string;
  playbackUrl: string;
  integration: string;
  fullReportAccess: string;
  easyCare: string;
  dealer: string;
  resellerReportingEnabled: string;
  restrictFolderSharingToAdmins: string;
  createdAt: string;
}
export enum IQuoteSettings {
  DOLLAR = 'dollar',
  ITEM = 'item',
}

//CUSTOMER
export type Customer = IMSFields & {
  preventUserEditAccess: string;
  hasReadOnlyVideoAttributeConstraint: string;
  hasLockableVideoAttribute: string;
  hasVideoAttributeDepartmentLimit: string;
  automotiveAccess: string;
  webShare: string;
  packageId: string;
  newPackageId: string;
  customerId: string;
  parentCustomerId: string;
  locationId?: any;
  resellerId: string;
  stripeId: string;
  codirectId?: any;
  infusionsoftContactId: string;
  infusionsoftCcid: string;
  infusionsoftSequence: string;
  deactivationDate: string;
  createdAt: string;
  timezone: string;
  weeklyReportEnabled: number;
  leaderboardReportEnabled: number;
  hasAudioDenoise: boolean;
  contractId: string;
  business: string;
  customerLogo: string;
  dealerGroup: string;
  crmId: string;
  crmProviderId: string;
  defaultTemplate: string;
  firstName: string;
  quoteSettings: string;
  lastName: string;
  title: string;
  legalFirstName: string;
  legalLastName: string;
  legalTitle: string;
  eLeadEnabled: string;
  streetAddress: string;
  city: string;
  state: string;
  cardOther: string;
  zip: string;
  country: string;
  phone1: string;
  phone2: string;
  fax1: string;
  email: string;
  url: string;
  markVideosAsSent: number | null;
  preloadTeleprompterScripts: number;
  connSpeed: string;
  referredBy: string;
  referralAgent: string;
  coagent: string;
  accountManager: string;
  signupDate: string;
  endDate?: any;
  trialEnd: string;
  maxUsers: string;
  totalSecondsAllowed: string;
  totalSecondsUsed: string;
  startPage: string;
  videoEmail: string;
  virtualRep: string;
  videoConference: string;
  videoOutlook: string;
  videoPartsSearch: string;
  videoBlast: string;
  videoOverlay: string;
  bandwidthAllowed: string;
  mailServer: string;
  crmReportEmail: string;
  videoRepEmail: string;
  active: string;
  emailIcon: string;
  streetAddressShipping: string;
  cityShipping: string;
  stateShipping: string;
  zipShipping: string;
  agreementLength: string;
  setupFee: string;
  monthlyFee: string;
  startingDate: string;
  shippingNumber: string;
  industry: string;
  numOfTemplates: string;
  integration: string;
  ownerId: string;
  verified: string;
  freeTrial: string;
  events: string;
  banner: string;
  bgImage: string;
  bgSlice: string;
  bgColor: string;
  txtColor1: string;
  txtColor2: string;
  maxUsersBasic: string;
  maxUsersAdvanced: string;
  maxUsersVideoEmail_1000: string;
  maxUsersVideoEmail_5000: string;
  maxUsersAdvanced_1000: string;
  maxUsersAdvanced_5000: string;
  maxUsersLiveConference: string;
  notes: string;
  carfax: string;
  slipstream: string;
  partnerType: string;
  cimProfileId?: any;
  isEmailTemplate: string;
  triggerStartDate: string;
  allowReplies: string;
  displayInReports: string;
  customGraphics: string;
  socialLinksLocked: string;
  launched: string;
  launchDate?: any;
  transcriptions: string;
  transcriptionLicenses: string;
  smsEnabled: string;
  smsProfileId: string;
  ssoId: string;
  droplrEnabled: string;
  landingPageBuilder: string;
  droplrLicenses: string;
  droplrTeamHash: string;
  dealerSocketId?: any;
  dealerSocketFranchiseId?: any;
  videoDeletionAccess: string;
  defaultSMSUserId?: any;
  departmentTagEnabled: string;
  salesforceAccountId: string;
  credit700Url: string;
  dealerComId?: any;
  dominionDealerId?: any;
  groupId: string;
  reactionsPreference?: any;
  leadsIntegrationActive?: boolean | number | null;
  salesforceIntegration: string;
  wheelsTV: string;
  allowDelete: string;
  hasMultiLocation: string;
  hideInventoryItemPrice: string;
  planEndsAt?: any;
  isPlanCancelled: string;
  newMaxUsers: string;
  videoExpiration: string;
  regionId: number;
  organizationId: number;
  hasCovideoMagic?: boolean;
  keyMetricsEnabled: boolean;
  liveAccess?: number;
  iPacketDealerId?: string;
  jdPowerVDPEnabled?: number;
  hasLandingPageVDP?: number;
  lesaAccess: number;
  autominerEnabled: boolean;
  autominerGroupId: string;
  oemDealerCode: string;
  oemAreaCode?: string;
  oemSubAreaCode?: string;
  leadsNotificationsAccess: string;
  emailsSentLimit: number;
  webAppVersionAccess: {
    v1: boolean;
    v2: boolean;
    v3: boolean;
    v4: boolean;
  };
  apiDocumentationAccess?: string;
  defaultLandingPageDesignId: number;
  isUsingLandingPageDesign: number;
  customerOrganizations?: CustomerOrganization[];
  hasNewFoldersAccess?: string;
  defaultBackgroundId: number;
  shareCooldownSeconds?: number;
  musicEnabled: number;
  spotlightEnabled: number;
  maxSpotlightPerMonth: number;
  isUsingVinReels: number;
  maxVinReelsPerMonth: number;
  quickVideoAvatarEnabled: number;
  maxQuickVideoAvatarPerMonth: number;
  suggestedContentEnabled: number;
  quickVideosEnabled: number;
  aiMusicEnabled: number;
  mapIframeEmbedCode: string;
};
export enum LANGAUGES {
  EN = 'en',
  ES = 'es',
  FR = 'fr',
}

export type UserLanguage = LANGAUGES.EN | LANGAUGES.ES | LANGAUGES.FR;

// ROOT USER
export interface RootUser {
  /**
   * @desc
   * Company admin is user who has access
   * values of "3" and "4"
   */
  isCompanyAdmin: boolean;
  isAutomotive: boolean;
  user: IUser;
  uid: string;
  userId: string;
  customerId: string;
  resellerId: string;
  bouncedEmail: string;
  firstName: string;
  admin: string;
  trial: boolean;
  access: string;
  verified: string;
  captions: boolean;
  vin: boolean;
  iat: number;
  exp: number;
  showWelcome: string;
  salesforceIntegration: boolean;
  wheelsTV: boolean;
  webEmailEnabled: boolean;
  allowDelete: boolean;
  manualPassword: boolean;
  cellPhone: string;
  deactivationDate: string;
  webShare: boolean;
  intercom: IIntercom;
  id: string;
  parentUserId: string;
  parentCustomerId: string;
  username: string;
  lastName: string;
  webName1: string;
  webName2: string;
  conferenceName?: any;
  email: string;
  phone1: string;
  phone2: string;
  signupDate: string;
  purchaseDate: string;
  emailVerified: boolean;
  dept: string;
  Dept: string;
  website: string;
  title: string;
  csAdmin: string;
  resellerAdmin?: any;
  videoCount: string;
  defaultVolume: string;
  addedBy: string;
  betaj: string;
  onVirtualRep: string;
  videoRepTimeout: string;
  videoRepDept: string;
  betaMember: string;
  videoConfUser: string;
  emailIcon: string;
  playInEmail: boolean;
  notifyMe: boolean;
  playButtonPosition: string;
  webmail: string;
  integration: string;
  totalSecondsAllowed: string;
  totalSecondsUsed: string;
  BSORFLN?: any;
  fusemail: boolean;
  distributor: string;
  packageId: string;
  trialAccount: boolean;
  affiliate: string;
  affiliateAccess: boolean;
  isEmailTemplate: boolean;
  isPending: boolean;
  qraAccess: boolean;
  loginDate: string;
  totalLogins: string;
  android: boolean;
  mobileEmailAccess: boolean;
  mobileSMSAccess: boolean;
  mobileShareAccess: boolean;
  mobileDownloadAccess: boolean;
  desktopEmailAccess: boolean;
  webEmailAccess: boolean;
  allowReplies: boolean;
  globalBcc: string;
  infusionSoftCID: string;
  defaultSignature: string;
  timezone: string;
  transcriptionAccess: boolean;
  droplrAccess: boolean;
  transcriptionDefaultEnabled: boolean;
  autoTranscribe: boolean;
  phoneId: string;
  onboarded: boolean;
  eleadDMSEmployeeID?: any;
  eleadEnabled: string;
  nmls: string;
  reactionsPreference?: any;
  vdpEnabled: boolean;
  onboardedFreemium: string;
  automotiveRole: string;
  workingHours: IWorkingHours;
  reseller: IReseller;
  customer: Customer;
  interactiveCTAs?: InteractiveCTA[] | null;
  customerAccess: boolean;
  userOrganizationAccess?: UserOrganizationAccess[];
  upgradeRequestedOn?: string;
  trialEmailsSent: number;
  videoShareLinkText?: string;
  email2?: string;
  isUsingVinReels: number;
  language: UserLanguage;
  isPodiumConnected: number;
  spotlightEnabled: number;
  quickVideoAvatarEnabled: number;
  landingPageDesignAdministration: number;
}

// Decoded token
export interface IDecodedToken {
  user: IUser;
  uid: string;
  userId: string;
  customerId: string;
  resellerId: string;
  firstName: string;
  admin: boolean;
  trial: boolean;
  access: string;
  verified: string;
  captions: boolean;
  vin: boolean;
  iat: number;
  exp: number;
  showWelcome: boolean;
  salesforceIntegration: boolean;
  webEmailEnabled: boolean;
  allowDelete: boolean;
  webShare: boolean;
  intercom: IIntercom;
}
export interface IUserRoles {
  isCompanyAdmin: boolean;
  /**
   * @desc
   * User is automotive if they have automotiveAccess = 1 and have an automotive role
   */
  isAutomotive: boolean;
  /**
   * @desc
   * User is reseller admin if they are company admin and they have resellerAdmin = true || resellerAdmin = '1'
   */
  isResellerAdmin: boolean;
  isOrganizationAdmin: boolean;
  isMultiLocation: boolean;
  superAdminRole: SuperAdminRole;
  hasRegions: boolean;
  isAutomotiveServiceRole: boolean;
  isAutomotiveSalesRole: boolean;
  isIMSEnabled: boolean;
  isVdpEnabled: boolean;
  isVdpAllowed: boolean;
  hasLiveVideoAccess: boolean;
  hasLeadsReports: boolean;
}

export interface ICombinedUserData extends RootUser, IUserRoles {
  preventUserEditAccess: boolean;
  trialDays: number;
  trialExpired: boolean;
  credit700Url: string;
  guideCategoriesAdministration: boolean;
}

export enum THEME_NAME {
  AUTOMOTIVE = 'automotive',
  COVIDEO = 'covideo',
  KENNECTED = 'kennected',
  PROVIDEO = 'provideo',
  APP_2_VID = 'app2vid',
  BRANDING = 'branding',
  CDK = 'cdk',
}
